import { supabase } from './supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function callEdgeFunction(name: string, body: Record<string, unknown>, timeoutMs = 30000) {
  const url = `${SUPABASE_URL}/functions/v1/${name}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token || ANON_KEY;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        apikey: ANON_KEY,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `Request failed (${response.status})` }));
    throw new Error(errorData.error || `Request failed (${response.status})`);
  }

  const data = await response.json();
  if (data.error) {
    throw new Error(data.error);
  }
  return data;
}

export const vaultApi = {
  getStatus: (tenantId: string) =>
    callEdgeFunction('vault-proxy', { action: 'get', tenant_id: tenantId }),

  upsert: (tenantId: string, vaultData: Record<string, unknown>) =>
    callEdgeFunction('vault-proxy', { action: 'upsert', tenant_id: tenantId, vault_data: vaultData }),
};

export const xaiVideoApi = {
  start: (projectId: string, prompt: string, options?: { model?: string; duration?: number; resolution?: string; aspectRatio?: string }) =>
    callEdgeFunction('xai-video', { action: 'start', project_id: projectId, prompt, model: options?.model, duration: options?.duration, resolution: options?.resolution, aspect_ratio: options?.aspectRatio }, 30000),
  status: (projectId: string, requestId?: string) =>
    callEdgeFunction('xai-video', { action: 'status', project_id: projectId, request_id: requestId }, 30000),
  proxyUrl: (projectId: string) => {
    const url = `${SUPABASE_URL}/functions/v1/xai-video`;
    return `${url}?project_id=${encodeURIComponent(projectId)}&action=proxy`;
  },
};

export const aiApi = {
  chat: (tenantId: string, messages: Array<{ role: string; content: string }>, options?: { model?: string; max_tokens?: number; temperature?: number }) =>
    callEdgeFunction('ai-proxy', { tenant_id: tenantId, messages, ...options }),
  expandPrompt: (tenantId: string, concept: string, aspectRatio?: string, isEbookCover?: boolean) =>
    callEdgeFunction('ai-proxy', { tenant_id: tenantId, action: 'expand_prompt', concept, aspect_ratio: aspectRatio, is_ebook_cover: isEbookCover }, 30000),
  generateImage: (tenantId: string, prompt: string, style?: string, dimensions?: string, referenceImageUrl?: string, aspectRatio?: string, isEbookCover?: boolean) =>
    callEdgeFunction('ai-proxy', { tenant_id: tenantId, action: 'generate_image', prompt, style, dimensions, reference_image_url: referenceImageUrl, aspect_ratio: aspectRatio, is_ebook_cover: isEbookCover }, 90000),
};

export const researchApi = {
  search: (tenantId: string, query: string) =>
    callEdgeFunction('research-proxy', { action: 'search', tenant_id: tenantId, query }),
};

export const contentApi = {
  generate: (tenantId: string, brief: Record<string, unknown>, assetTypes: string[], promptText?: string, length?: string, skipCopyright?: boolean, branding?: { use_my_branding?: boolean; override_brand?: { business_name?: string; website_url?: string; contact_email?: string; contact_phone?: string; tagline?: string } | null }) => {
    const isComprehensive = length === 'comprehensive';
    return callEdgeFunction('content-proxy', { action: 'generate', tenant_id: tenantId, brief, asset_types: assetTypes, prompt_text: promptText, length, skip_copyright: skipCopyright, use_my_branding: branding?.use_my_branding, override_brand: branding?.override_brand }, isComprehensive ? 600000 : 600000);
  },
  checkCompliance: (tenantId: string, assetId: string, content: string) =>
    callEdgeFunction('content-proxy', { action: 'check_compliance', tenant_id: tenantId, asset_id: assetId, content }, 30000),
  checkComplianceStatus: (tenantId: string, assetId: string, content: string) =>
    callEdgeFunction('content-proxy', { action: 'check_compliance_status', tenant_id: tenantId, asset_id: assetId, content }, 30000),
  fetchReport: (tenantId: string, assetId: string) =>
    callEdgeFunction('content-proxy', { action: 'fetch_report', tenant_id: tenantId, asset_id: assetId }, 30000),
  transformContent: (tenantId: string, assetId: string, content: string, transformType: 'humanize' | 'summarize' | 'paraphrase') =>
    callEdgeFunction('content-proxy', { action: 'transform_content', tenant_id: tenantId, asset_id: assetId, content, transform_type: transformType }, 120000),
  rewritePassage: (tenantId: string, assetId: string, passage: string) =>
    callEdgeFunction('content-proxy', { action: 'rewrite_passage', tenant_id: tenantId, asset_id: assetId, content: passage, transform_type: 'humanize' }, 120000),
  autoFixCompliance: (tenantId: string, assetId: string) =>
    callEdgeFunction('content-proxy', { action: 'auto_fix_compliance', tenant_id: tenantId, asset_id: assetId }, 180000),
  getQuetextPlan: (tenantId: string) =>
    callEdgeFunction('content-proxy', { action: 'get_quetext_plan', tenant_id: tenantId }, 30000),
  downloadReportPdf: async (tenantId: string, assetId: string, reportType: 'plagiarism' | 'ai') => {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token || ANON_KEY;
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/content-proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`, apikey: ANON_KEY },
      body: JSON.stringify({ action: 'download_report_pdf', tenant_id: tenantId, asset_id: assetId, report_type: reportType }),
    });
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({ error: `Download failed (${resp.status})` }));
      throw new Error(errData.error || `Download failed (${resp.status})`);
    }
    const contentType = resp.headers.get('Content-Type') || '';
    if (contentType.includes('application/pdf')) {
      return await resp.blob();
    }
    return await resp.json();
  },
  generateCitations: (tenantId: string, assetId: string, content: string, citationStyle: string) =>
    callEdgeFunction('content-proxy', { action: 'generate_citations', tenant_id: tenantId, asset_id: assetId, content, citation_style: citationStyle }, 90000),
  aiTutorParaphrase: (tenantId: string, assetId: string, content: string) =>
    callEdgeFunction('content-proxy', { action: 'ai_tutor_paraphrase', tenant_id: tenantId, asset_id: assetId, content }, 120000),
};

export const deployApi = {
  deploy: (tenantId: string, assetId: string, targetId: string) =>
    callEdgeFunction('deploy-proxy', { action: 'deploy', tenant_id: tenantId, asset_id: assetId, target_id: targetId }),
  deployBundle: (tenantId: string, targetId: string, bundleTitle: string, bundleContent: string) =>
    callEdgeFunction('deploy-proxy', { action: 'deploy_bundle', tenant_id: tenantId, target_id: targetId, bundle_title: bundleTitle, bundle_content: bundleContent }),
  addTarget: (tenantId: string, name: string, platform: string, endpoint: string, token: string) =>
    callEdgeFunction('deploy-proxy', { action: 'add_target', tenant_id: tenantId, name, platform, endpoint, token }),
  listTargets: (tenantId: string) =>
    callEdgeFunction('deploy-proxy', { action: 'list_targets', tenant_id: tenantId }),
};

export const emailVerificationApi = {
  status: () => callEdgeFunction('email-verification', { action: 'status' }),
  sendCode: () => callEdgeFunction('email-verification', { action: 'send_code' }),
  verifyCode: (code: string) => callEdgeFunction('email-verification', { action: 'verify_code', code }),
};

export const affiliateApi = {
  generateReferralCode: (userId: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'generate_referral_code', user_id: userId }),
  generateLink: (userId: string, targetUrl: string, name?: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'generate_link', user_id: userId, target_url: targetUrl, name }),
  trackClick: (referralCode: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'track_click', referral_code: referralCode }),
  testLink: (linkId: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'test_link', link_id: linkId }),
  requestPayout: (affiliateId: string, amount: number, method?: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'request_payout', affiliate_id: affiliateId, amount, method }),
  trackReferral: (affiliateId: string, referralCode: string, referredEmail?: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'track_referral', affiliate_id: affiliateId, referral_code: referralCode, referred_email: referredEmail }),
  verifyCrossPlatform: (userId: string, platform: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'verify_cross_platform', user_id: userId, platform }),
  sendSkypeMessage: (skypeId: string, message: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'send_skype_message', skype_id: skypeId, message }),
  adminListAffiliates: (statusFilter?: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_list_affiliates', status_filter: statusFilter }),
  adminApproveAffiliate: (affiliateUserId: string, commissionRate?: number) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_approve_affiliate', affiliate_user_id: affiliateUserId, commission_rate: commissionRate }),
  adminRejectAffiliate: (affiliateUserId: string, adminNotes?: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_reject_affiliate', affiliate_user_id: affiliateUserId, admin_notes: adminNotes }),
  adminResendApprovalEmail: (affiliateUserId: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_resend_approval_email', affiliate_user_id: affiliateUserId }),
  adminSetPayoutMode: (affiliateUserId: string, payoutMode: 'manual' | 'auto', autoPayoutThreshold?: number) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_set_payout_mode', affiliate_user_id: affiliateUserId, payout_mode: payoutMode, auto_payout_threshold: autoPayoutThreshold }),
  adminSetCustomRates: (affiliateUserId: string, customInitialRate: number | null, customLongtermRate: number | null) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_set_custom_rates', affiliate_user_id: affiliateUserId, custom_initial_rate: customInitialRate, custom_longterm_rate: customLongtermRate }),
  adminListPayouts: (statusFilter?: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_list_payouts', status_filter: statusFilter }),
  adminSendAffiliateEmail: (senderUserId: string, subject: string, bodyHtml: string, recipientMode: string, recipientUserIds?: string[]) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_send_affiliate_email', sender_user_id: senderUserId, subject, body_html: bodyHtml, recipient_mode: recipientMode, recipient_user_ids: recipientUserIds }, 120000),
  adminListCampaigns: () =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_list_campaigns' }),
  adminListRecipients: (campaignId: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'admin_list_recipients', campaign_id: campaignId }),
  onboardAffiliate: (userId: string) =>
    callEdgeFunction('affiliate-proxy', { action: 'onboard_affiliate', user_id: userId }, 120000),
};

export const socialApi = {
  postNow: (tenantId: string, platform: string, content: string, postId?: string, mediaUrl?: string, mediaType?: string, contentType?: string, privacyLevel?: string, title?: string, tiktokEngagement?: { allowComment?: boolean | null; allowDuet?: boolean | null; allowStitch?: boolean | null }) =>
    callEdgeFunction('social-poster', { action: 'post_now', tenant_id: tenantId, platform, content, title, post_id: postId, media_url: mediaUrl, media_type: mediaType, content_type: contentType, privacy_level: privacyLevel, tiktok_allow_comment: tiktokEngagement?.allowComment, tiktok_allow_duet: tiktokEngagement?.allowDuet, tiktok_allow_stitch: tiktokEngagement?.allowStitch }, 120000),
  getTikTokCreatorInfo: (tenantId: string, accountId: string) =>
    callEdgeFunction('social-poster', { action: 'get_tiktok_creator_info', tenant_id: tenantId, account_id: accountId }, 30000),
  publishScheduled: (tenantId: string) =>
    callEdgeFunction('social-poster', { action: 'publish_scheduled', tenant_id: tenantId }, 90000),
  connectAccount: (tenantId: string, platform: string, redirectUri?: string, appOrigin?: string, metadata?: Record<string, string>) =>
    callEdgeFunction('social-poster', { action: 'connect_account', tenant_id: tenantId, platform, redirect_uri: redirectUri, app_origin: appOrigin, metadata }, 30000),
  oauthExchange: (tenantId: string, code: string, state?: string) =>
    callEdgeFunction('social-poster', { action: 'oauth_exchange', tenant_id: tenantId, code, state }, 30000),
  listAccounts: (tenantId: string) =>
    callEdgeFunction('social-poster', { action: 'list_accounts', tenant_id: tenantId }, 30000),
  syncAllAccounts: (tenantId: string) =>
    callEdgeFunction('social-poster', { action: 'sync_all_accounts', tenant_id: tenantId }, 30000),
  disconnectAccount: (tenantId: string, accountId: string) =>
    callEdgeFunction('social-poster', { action: 'disconnect_account', tenant_id: tenantId, account_id: accountId }, 30000),
  getAnalytics: (tenantId: string, accountId: string, since?: string, until?: string) =>
    callEdgeFunction('social-poster', { action: 'get_analytics', tenant_id: tenantId, account_id: accountId, since, until }, 30000),
  getAccountAnalytics: (tenantId: string, accountId: string, since?: string, until?: string) =>
    callEdgeFunction('social-poster', { action: 'get_account_analytics', tenant_id: tenantId, account_id: accountId, since, until }, 30000),
  syncPostAnalytics: (tenantId: string) =>
    callEdgeFunction('social-poster', { action: 'sync_post_analytics', tenant_id: tenantId }, 60000),
  syncPostStatus: (tenantId: string) =>
    callEdgeFunction('social-poster', { action: 'sync_post_status', tenant_id: tenantId }, 60000),
  readComments: (tenantId: string, accountId: string, limit?: number) =>
    callEdgeFunction('social-poster', { action: 'read_comments', tenant_id: tenantId, account_id: accountId, limit }, 30000),
  replyComment: (tenantId: string, commentId: string, text: string, accountId: string) =>
    callEdgeFunction('social-poster', { action: 'reply_comment', tenant_id: tenantId, comment_id: commentId, text, account_id: accountId }, 30000),
  readMessages: (tenantId: string, accountId: string, limit?: number) =>
    callEdgeFunction('social-poster', { action: 'read_messages', tenant_id: tenantId, account_id: accountId, limit }, 30000),
  replyMessage: (tenantId: string, conversationId: string, text: string) =>
    callEdgeFunction('social-poster', { action: 'reply_message', tenant_id: tenantId, conversation_id: conversationId, text }, 30000),
  getPostStatus: (tenantId: string, postId: string) =>
    callEdgeFunction('social-poster', { action: 'get_post_status', tenant_id: tenantId, post_id: postId }, 30000),
  retryPost: (tenantId: string, postId: string) =>
    callEdgeFunction('social-poster', { action: 'retry_post', tenant_id: tenantId, post_id: postId }, 30000),
  deletePost: (tenantId: string, postId: string) =>
    callEdgeFunction('social-poster', { action: 'delete_post', tenant_id: tenantId, post_id: postId }, 30000),
  bulkImport: (tenantId: string, posts: Array<Record<string, unknown>>) =>
    callEdgeFunction('social-poster', { action: 'bulk_import', tenant_id: tenantId, posts }, 60000),
  listPendingProfiles: (tenantId: string, connectionId: string) =>
    callEdgeFunction('social-poster', { action: 'list_pending_profiles', tenant_id: tenantId, connection_id: connectionId }, 30000),
  selectPendingProfile: (tenantId: string, connectionId: string, platformAccountId: string, options?: { pageIds?: string[]; platform?: string }) =>
    callEdgeFunction('social-poster', {
      action: 'select_pending_profile',
      tenant_id: tenantId,
      connection_id: connectionId,
      ...(options?.pageIds ? { page_ids: options.pageIds, platform: options.platform } : { platform_account_id: platformAccountId }),
    }, 30000),
};

export const webhookOutletsApi = {
  list: (tenantId: string) =>
    callEdgeFunction('publish-webhook', { action: 'list_destinations', tenant_id: tenantId }),
  create: (tenantId: string, name: string, url: string, autoPublish: boolean) =>
    callEdgeFunction('publish-webhook', { action: 'create_destination', tenant_id: tenantId, name, url, auto_publish: autoPublish }),
  revealSecret: (tenantId: string, destinationId: string) =>
    callEdgeFunction('publish-webhook', { action: 'reveal_secret', tenant_id: tenantId, destination_id: destinationId }),
  update: (tenantId: string, destinationId: string, patch: { enabled?: boolean; auto_publish?: boolean }) =>
    callEdgeFunction('publish-webhook', { action: 'update_destination', tenant_id: tenantId, destination_id: destinationId, ...patch }),
  remove: (tenantId: string, destinationId: string) =>
    callEdgeFunction('publish-webhook', { action: 'delete_destination', tenant_id: tenantId, destination_id: destinationId }),
  listDeliveries: (tenantId: string, destinationId: string) =>
    callEdgeFunction('publish-webhook', { action: 'list_deliveries', tenant_id: tenantId, destination_id: destinationId }),
  sendTest: (tenantId: string, destinationId: string) =>
    callEdgeFunction('publish-webhook', { action: 'send_test', tenant_id: tenantId, destination_id: destinationId }, 20000),
  sendContent: (tenantId: string, destinationIds: string[], contentId: string, resend = false) =>
    callEdgeFunction('publish-webhook', { action: 'send_content', tenant_id: tenantId, destination_ids: destinationIds, content_id: contentId, resend }, 60000),
};

export const publishingApi = {
  addChannel: (tenantId: string, channelType: string, platform: string, name: string, config: Record<string, unknown>) =>
    callEdgeFunction('publishing-proxy', { action: 'add_channel', tenant_id: tenantId, channel_type: channelType, platform, name, config }),
  listChannels: (tenantId: string) =>
    callEdgeFunction('publishing-proxy', { action: 'list_channels', tenant_id: tenantId }),
  deleteChannel: (tenantId: string, channelId: string) =>
    callEdgeFunction('publishing-proxy', { action: 'delete_channel', tenant_id: tenantId, channel_id: channelId }),
  publish: (tenantId: string, channelId: string, title: string, content: string, contentType: string, assetId?: string, scheduledFor?: string) =>
    callEdgeFunction('publishing-proxy', { action: 'publish', tenant_id: tenantId, channel_id: channelId, title, content, content_type: contentType, asset_id: assetId, scheduled_for: scheduledFor }, 60000),
  publishMulti: (tenantId: string, channelIds: string[], title: string, content: string, contentType: string, assetId?: string) =>
    callEdgeFunction('publishing-proxy', { action: 'publish_multi', tenant_id: tenantId, channel_ids: channelIds, title, content, content_type: contentType, asset_id: assetId }, 90000),
  listPosts: (tenantId: string) =>
    callEdgeFunction('publishing-proxy', { action: 'list_posts', tenant_id: tenantId }),
};

export const podcastApi = {
  generate: (projectId: string, tenantId: string, options?: { voice?: string; model?: string; instructions?: string }) =>
    callEdgeFunction('podcast-generate', { action: 'generate', project_id: projectId, tenant_id: tenantId, voice: options?.voice, model: options?.model, instructions: options?.instructions }, 300000),
  previewVoice: (voice: string, tenantId: string, options?: { model?: string; instructions?: string; previewText?: string }) =>
    callEdgeFunction('podcast-generate', { action: 'preview', voice, tenant_id: tenantId, model: options?.model, instructions: options?.instructions, preview_text: options?.previewText }, 30000),
};

export { callEdgeFunction };

export const savedImagePromptsApi = {
  save: async (userId: string, targetType: string, title: string, promptText: string, sourceAssetId?: string) => {
    const { error } = await supabase.from('saved_image_prompts').insert({
      user_id: userId,
      target_type: targetType,
      title,
      prompt_text: promptText,
      source_asset_id: sourceAssetId ?? null,
    });
    return { error };
  },
  saveBatch: async (userId: string, targetType: string, items: { title: string; promptText: string }[], sourceAssetId?: string) => {
    const rows = items.map(it => ({
      user_id: userId,
      target_type: targetType,
      title: it.title,
      prompt_text: it.promptText,
      source_asset_id: sourceAssetId ?? null,
    }));
    const { error } = await supabase.from('saved_image_prompts').insert(rows);
    return { error };
  },
  list: async (userId: string, targetType: string) => {
    const { data, error } = await supabase
      .from('saved_image_prompts')
      .select('id, title, prompt_text, used, created_at')
      .eq('user_id', userId)
      .eq('target_type', targetType)
      .order('created_at', { ascending: false });
    return { prompts: data ?? [], error };
  },
  markUsed: async (id: string) => {
    await supabase.from('saved_image_prompts').update({ used: true }).eq('id', id);
  },
  delete: async (id: string) => {
    await supabase.from('saved_image_prompts').delete().eq('id', id);
  },
};

export interface ModuleImage {
  id: string;
  module_type: string;
  image_url: string;
  prompt_text: string;
  title: string;
  dimensions: string | null;
  source: string;
  created_at: string;
}

export interface ModulePrompt {
  id: string;
  module_type: string;
  title: string;
  prompt_text: string;
  source: string;
  used: boolean;
  created_at: string;
}

export const moduleImagesApi = {
  listImages: async (userId: string, moduleType: string) => {
    const { data, error } = await supabase
      .from('module_images')
      .select('id, module_type, image_url, prompt_text, title, dimensions, source, created_at')
      .eq('user_id', userId)
      .eq('module_type', moduleType)
      .order('created_at', { ascending: false });
    return { images: (data ?? []) as ModuleImage[], error };
  },
  saveImage: async (userId: string, moduleType: string, imageUrl: string, promptText: string, title: string, source: string, dimensions?: string) => {
    const { error } = await supabase.from('module_images').insert({
      user_id: userId, module_type: moduleType, image_url: imageUrl, prompt_text: promptText, title, source, dimensions: dimensions ?? null,
    });
    return { error };
  },
  updateImage: async (id: string, updates: { title?: string; prompt_text?: string }) => {
    const { error } = await supabase.from('module_images').update(updates).eq('id', id);
    return { error };
  },
  deleteImage: async (id: string) => {
    const { error } = await supabase.from('module_images').delete().eq('id', id);
    return { error };
  },

  listPrompts: async (userId: string, moduleType: string) => {
    const { data, error } = await supabase
      .from('module_prompts')
      .select('id, module_type, title, prompt_text, source, used, created_at')
      .eq('user_id', userId)
      .eq('module_type', moduleType)
      .order('created_at', { ascending: false });
    return { prompts: (data ?? []) as ModulePrompt[], error };
  },
  savePrompt: async (userId: string, moduleType: string, title: string, promptText: string, source: string) => {
    const { error } = await supabase.from('module_prompts').insert({
      user_id: userId, module_type: moduleType, title, prompt_text: promptText, source,
    });
    return { error };
  },
  savePromptBatch: async (userId: string, moduleType: string, items: { title: string; promptText: string }[], source: string) => {
    const rows = items.map(it => ({
      user_id: userId, module_type: moduleType, title: it.title, prompt_text: it.promptText, source,
    }));
    const { error } = await supabase.from('module_prompts').insert(rows);
    return { error };
  },
  updatePrompt: async (id: string, updates: { title?: string; prompt_text?: string; used?: boolean }) => {
    const { error } = await supabase.from('module_prompts').update(updates).eq('id', id);
    return { error };
  },
  deletePrompt: async (id: string) => {
    const { error } = await supabase.from('module_prompts').delete().eq('id', id);
    return { error };
  },
};

export const leadGenApi = {
  createCampaign: (tenantId: string, name: string, description: string, searchCriteria: Record<string, unknown>, maxProspects?: number) =>
    callEdgeFunction('lead-gen-proxy', { action: 'create_campaign', tenant_id: tenantId, user_id: tenantId, name, description, searchCriteria, maxProspects }, 30000),
  listCampaigns: (tenantId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'list_campaigns', tenant_id: tenantId, user_id: tenantId }, 30000),
  getCampaign: (tenantId: string, campaignId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'get_campaign', tenant_id: tenantId, user_id: tenantId, campaignId }, 30000),
  deleteCampaign: (tenantId: string, campaignId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'delete_campaign', tenant_id: tenantId, user_id: tenantId, campaignId }, 30000),
  searchProspects: (tenantId: string, campaignId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'search_prospects', tenant_id: tenantId, user_id: tenantId, campaignId }, 120000),
  updateProspectStatus: (tenantId: string, prospectId: string, status: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'update_prospect_status', tenant_id: tenantId, user_id: tenantId, prospectId, status }, 30000),
  draftOutreach: (tenantId: string, prospectId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'draft_outreach', tenant_id: tenantId, user_id: tenantId, prospectId }, 60000),
  updateOutreach: (tenantId: string, prospectId: string, subject: string, body: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'update_outreach', tenant_id: tenantId, user_id: tenantId, prospectId, subject, body }, 30000),
  sendOutreach: (tenantId: string, prospectId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'send_outreach', tenant_id: tenantId, user_id: tenantId, prospectId }, 60000),
  getStats: (tenantId: string) =>
    callEdgeFunction('lead-gen-proxy', { action: 'get_stats', tenant_id: tenantId, user_id: tenantId }, 30000),
};

export const agentApi = {
  startRun: (tenantId: string, goal: string, conversationHistory?: Array<{ role: string; content: string }>, branding?: Record<string, unknown>) =>
    callEdgeFunction('marketing-agent', { action: 'start_run', tenant_id: tenantId, user_id: tenantId, goal, conversation_history: conversationHistory, ...(branding || {}) }, 120000),
  continueRun: (tenantId: string, runId: string, message: string) =>
    callEdgeFunction('marketing-agent', { action: 'continue', tenant_id: tenantId, user_id: tenantId, run_id: runId, message }, 120000),
  approvePlan: (tenantId: string, runId: string, timeframeDays?: number) =>
    callEdgeFunction('marketing-agent', { action: 'approve_plan', tenant_id: tenantId, user_id: tenantId, run_id: runId, timeframe_days: timeframeDays }, 120000),
  retryPlan: (tenantId: string, runId: string) =>
    callEdgeFunction('marketing-agent', { action: 'retry_plan', tenant_id: tenantId, user_id: tenantId, run_id: runId }, 30000),
  deleteRun: (tenantId: string, runId: string) =>
    callEdgeFunction('marketing-agent', { action: 'delete_run', tenant_id: tenantId, user_id: tenantId, run_id: runId }, 30000),
  updateRunNotes: (tenantId: string, runId: string, notes: string) =>
    callEdgeFunction('marketing-agent', { action: 'update_run_notes', tenant_id: tenantId, user_id: tenantId, run_id: runId, notes }, 30000),
  rejectPlan: (tenantId: string, runId: string, feedback?: string) =>
    callEdgeFunction('marketing-agent', { action: 'reject_plan', tenant_id: tenantId, user_id: tenantId, run_id: runId, feedback }, 30000),
  stopRun: (tenantId: string, runId: string) =>
    callEdgeFunction('marketing-agent', { action: 'stop_run', tenant_id: tenantId, user_id: tenantId, run_id: runId }, 30000),
  resumeRun: (tenantId: string, runId: string, message?: string) =>
    callEdgeFunction('marketing-agent', { action: 'resume_run', tenant_id: tenantId, user_id: tenantId, run_id: runId, message }, 120000),
  getRunStatus: (tenantId: string, runId: string) =>
    callEdgeFunction('marketing-agent', { action: 'get_run_status', tenant_id: tenantId, user_id: tenantId, run_id: runId }, 30000),
  listRuns: (tenantId: string, limit?: number) =>
    callEdgeFunction('marketing-agent', { action: 'list_runs', tenant_id: tenantId, user_id: tenantId, limit }, 30000),
  getPerformance: (tenantId: string, daysBack?: number) =>
    callEdgeFunction('marketing-agent', { action: 'get_performance', tenant_id: tenantId, user_id: tenantId, days_back: daysBack }, 30000),
  listGoals: (tenantId: string) =>
    callEdgeFunction('marketing-agent', { action: 'list_goals', tenant_id: tenantId, user_id: tenantId }, 30000),
  createGoal: (tenantId: string, title: string, goalText: string, scheduleType?: string, scheduleInterval?: number) =>
    callEdgeFunction('marketing-agent', { action: 'create_goal', tenant_id: tenantId, user_id: tenantId, title, goal_text: goalText, schedule_type: scheduleType, schedule_interval: scheduleInterval }, 30000),
  updateGoal: (tenantId: string, goalId: string, updates: Record<string, unknown>) =>
    callEdgeFunction('marketing-agent', { action: 'update_goal', tenant_id: tenantId, user_id: tenantId, goal_id: goalId, ...updates }, 30000),
  deleteGoal: (tenantId: string, goalId: string) =>
    callEdgeFunction('marketing-agent', { action: 'delete_goal', tenant_id: tenantId, user_id: tenantId, goal_id: goalId }, 30000),

  // ── Blog Rotation ──────────────────────────────────────────
  listBlogSilos: (tenantId: string) =>
    callEdgeFunction('marketing-agent', { action: 'list_blog_silos', tenant_id: tenantId, user_id: tenantId }, 30000),
  createBlogSilo: (tenantId: string, name: string, pillarConcept: string, targetAudience: string, priority: number) =>
    callEdgeFunction('marketing-agent', { action: 'create_blog_silo', tenant_id: tenantId, user_id: tenantId, name, pillar_concept: pillarConcept, target_audience: targetAudience, priority }, 30000),
  updateBlogSilo: (tenantId: string, siloId: string, updates: Record<string, unknown>) =>
    callEdgeFunction('marketing-agent', { action: 'update_blog_silo', tenant_id: tenantId, user_id: tenantId, silo_id: siloId, ...updates }, 30000),
  deleteBlogSilo: (tenantId: string, siloId: string) =>
    callEdgeFunction('marketing-agent', { action: 'delete_blog_silo', tenant_id: tenantId, user_id: tenantId, silo_id: siloId }, 30000),
  listBlogKeywords: (tenantId: string, siloId?: string) =>
    callEdgeFunction('marketing-agent', { action: 'list_blog_keywords', tenant_id: tenantId, user_id: tenantId, silo_id: siloId }, 30000),
  addBlogKeyword: (tenantId: string, siloId: string, keyword: string, searchIntent: string, priority: number, isLongTail: boolean) =>
    callEdgeFunction('marketing-agent', { action: 'add_blog_keyword', tenant_id: tenantId, user_id: tenantId, silo_id: siloId, keyword, search_intent: searchIntent, priority, is_long_tail: isLongTail }, 30000),
  updateBlogKeyword: (tenantId: string, keywordId: string, updates: Record<string, unknown>) =>
    callEdgeFunction('marketing-agent', { action: 'update_blog_keyword', tenant_id: tenantId, user_id: tenantId, keyword_id: keywordId, ...updates }, 30000),
  deleteBlogKeyword: (tenantId: string, keywordId: string) =>
    callEdgeFunction('marketing-agent', { action: 'delete_blog_keyword', tenant_id: tenantId, user_id: tenantId, keyword_id: keywordId }, 30000),
  triggerNextKeyword: (tenantId: string) =>
    callEdgeFunction('marketing-agent', { action: 'trigger_next_keyword', tenant_id: tenantId, user_id: tenantId }, 30000),
  createBlogRotationGoal: (tenantId: string, title: string, scheduleType: string, scheduleInterval: number) =>
    callEdgeFunction('marketing-agent', { action: 'create_blog_rotation_goal', tenant_id: tenantId, user_id: tenantId, title, schedule_type: scheduleType, schedule_interval: scheduleInterval }, 30000),
  claimSeededSilos: (tenantId: string) =>
    callEdgeFunction('marketing-agent', { action: 'claim_seeded_silos', tenant_id: tenantId, user_id: tenantId }, 30000),
  listRotationGoals: (tenantId: string) =>
    callEdgeFunction('marketing-agent', { action: 'list_rotation_goals', tenant_id: tenantId, user_id: tenantId }, 30000),
  deleteRotationGoal: (tenantId: string, goalId: string) =>
    callEdgeFunction('marketing-agent', { action: 'delete_rotation_goal', tenant_id: tenantId, user_id: tenantId, goal_id: goalId }, 30000),
  toggleRotationGoal: (tenantId: string, goalId: string) =>
    callEdgeFunction('marketing-agent', { action: 'toggle_rotation_goal', tenant_id: tenantId, user_id: tenantId, goal_id: goalId }, 30000),
  resetBlogKeyword: (tenantId: string, keywordId: string) =>
    callEdgeFunction('marketing-agent', { action: 'reset_blog_keyword', tenant_id: tenantId, user_id: tenantId, keyword_id: keywordId }, 30000),
  listAllBlogKeywords: (tenantId: string) =>
    callEdgeFunction('marketing-agent', { action: 'list_blog_keywords', tenant_id: tenantId, user_id: tenantId }, 30000),
};

export interface OnboardingResult {
  content_assets: Array<{ asset_id: string; asset_type: string; label: string; preview: string }>;
  email_sequence: { name: string; email_count: number; preview: string } | null;
  platform_suggestions: Array<{ platform: string; reason: string; priority: 'high' | 'medium' | 'low' }>;
}

export const onboardingApi = {
  generateStarterContent: (tenantId: string, businessName: string, industry: string, targetAudience: string) =>
    callEdgeFunction('content-proxy', {
      action: 'generate',
      tenant_id: tenantId,
      brief: {
        topic: `Welcome content for ${businessName} in the ${industry} industry. Target audience: ${targetAudience || 'general consumers'}. Generate a blog post introducing the business, a social media announcement post, and a lead nurture email.`,
        keywords: [industry, 'welcome', 'introduction', businessName],
        summary: `Starter content pack for a new ${industry} business on Aurelian Marketing Engine.`,
      },
      asset_types: ['blog_post', 'social_media_post', 'email_html'],
      length: 'medium',
      persist: true,
      source: 'onboarding',
      platforms: ['linkedin', 'facebook'],
    }, 120000) as Promise<{ assets: Array<{ asset_id: string; asset_type: string; label: string; content?: string }> }>,

  suggestPlatforms: async (industry: string, targetAudience: string): Promise<Array<{ platform: string; reason: string; priority: 'high' | 'medium' | 'low' }>> => {
    const suggestions: Array<{ platform: string; reason: string; priority: 'high' | 'medium' | 'low' }> = [];
    const ind = (industry || '').toLowerCase();
    const aud = (targetAudience || '').toLowerCase();

    if (ind.includes('b2b') || ind.includes('saas') || ind.includes('tech') || ind.includes('software') || ind.includes('consult') || ind.includes('finance') || ind.includes('professional')) {
      suggestions.push({ platform: 'linkedin', reason: 'LinkedIn is the top platform for B2B and professional services — ideal for reaching decision-makers.', priority: 'high' });
    }
    if (ind.includes('ecommerce') || ind.includes('retail') || ind.includes('fashion') || ind.includes('beauty') || ind.includes('food') || ind.includes('restaurant') || ind.includes('fitness') || ind.includes('lifestyle')) {
      suggestions.push({ platform: 'instagram', reason: 'Instagram excels for visual products and lifestyle brands — perfect for showcasing your offerings.', priority: 'high' });
    }
    if (ind.includes('real estate') || ind.includes('property') || ind.includes('travel') || ind.includes('hospitality')) {
      suggestions.push({ platform: 'facebook', reason: 'Facebook reaches broad demographics and supports rich visual listings — great for real estate and travel.', priority: 'high' });
    }
    if (aud.includes('gen z') || aud.includes('young') || aud.includes('teen') || ind.includes('entertainment') || ind.includes('music') || ind.includes('gaming')) {
      suggestions.push({ platform: 'tiktok', reason: 'TikTok is where Gen Z and young audiences discover new brands — short-form video drives engagement.', priority: 'high' });
    }
    if (ind.includes('education') || ind.includes('tutorial') || ind.includes('how-to') || ind.includes('training')) {
      suggestions.push({ platform: 'youtube', reason: 'YouTube is the go-to platform for educational and tutorial content — build authority with video.', priority: 'high' });
    }

    if (!suggestions.find(s => s.platform === 'linkedin')) {
      suggestions.push({ platform: 'linkedin', reason: 'LinkedIn builds professional credibility and supports content publishing for any industry.', priority: 'medium' });
    }
    if (!suggestions.find(s => s.platform === 'facebook')) {
      suggestions.push({ platform: 'facebook', reason: 'Facebook has the broadest audience reach and supports diverse content formats.', priority: 'medium' });
    }
    if (!suggestions.find(s => s.platform === 'instagram')) {
      suggestions.push({ platform: 'instagram', reason: 'Instagram visual storytelling works across most consumer-facing industries.', priority: 'medium' });
    }
    suggestions.push({ platform: 'youtube', reason: 'YouTube video content boosts SEO and provides shareable assets for all industries.', priority: 'low' });

    return suggestions.slice(0, 5);
  },

  createWelcomeEmailSequence: async (userId: string, businessName: string, industry: string): Promise<{ name: string; email_count: number; preview: string } | null> => {
    try {
      const { data: existing } = await supabase
        .from('email_sequences')
        .select('id')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();
      if (existing) return null;

      const { data, error } = await supabase
        .from('email_sequences')
        .insert({
          user_id: userId,
          name: `Welcome Sequence — ${businessName}`,
          description: `Auto-generated welcome email sequence for ${businessName} (${industry}). Three emails introducing the brand, sharing value, and inviting engagement.`,
          trigger_type: 'signup',
          is_active: true,
        })
        .select('id')
        .single();

      if (error || !data) return null;

      const welcomeEmails = [
        { sort_order: 0, subject: `Welcome to ${businessName}! Here's what to expect`, body: `Hi there,\n\nWelcome to ${businessName}! We're thrilled to have you join our community.\n\nIn the ${industry} space, we know that staying ahead matters. Over the next few days, we'll share tips, insights, and resources to help you get the most out of what we offer.\n\nHere's what you can look forward to:\n- Industry insights and trends\n- Practical tips you can use right away\n- Exclusive offers and early access\n\nTalk soon,\nThe ${businessName} Team`, },
        { sort_order: 1, subject: `Here's something we think you'll love`, body: `Hi again,\n\nYesterday we welcomed you to ${businessName}. Today, we want to share something special.\n\nAs someone interested in ${industry}, you know that the right tools and insights make all the difference. That's why we're sharing a few ideas to help you get started.\n\nStay tuned for one more email tomorrow with an exclusive offer.\n\nBest,\nThe ${businessName} Team`, },
        { sort_order: 2, subject: `An exclusive welcome offer, just for you`, body: `Hi there,\n\nThis is our last welcome email, but it's the most exciting one!\n\nAs a thank you for joining ${businessName}, we'd like to invite you to take the next step. Whether it's exploring our ${industry} resources, connecting with our community, or trying out our platform, we're here to help.\n\nReply to this email if you have any questions — we read every response.\n\nWarmly,\nThe ${businessName} Team`, },
      ];

      for (const email of welcomeEmails) {
        await supabase.from('email_sequence_emails').insert({
          sequence_id: data.id,
          user_id: userId,
          subject: email.subject,
          body: email.body,
          delay_hours: email.sort_order * 24,
          sort_order: email.sort_order,
        });
      }

      return {
        name: `Welcome Sequence — ${businessName}`,
        email_count: 3,
        preview: welcomeEmails[0].subject,
      };
    } catch {
      return null;
    }
  },
};
